import config from '../config';

const CONNECTION_TOKEN_HEADER = 'X-Connect-Connection-Token';

export interface ChatAttachmentUpload {
  attachmentId: string;
  upload: {
    href: string;
    expiresAt: string;
    headers: Record<string, string>;
  };
}

export interface ChatAttachmentDownload {
  sizeInBytes: number;
  download: {
    href: string;
    expiresAt: string;
  };
}

export class ChatAttachmentApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | undefined
  ) {
    super(`Chat attachment API responded with status ${status}${code ? ` (${code})` : ''}`);
  }
}

const attachmentsUrl = (contactId: string, ...path: string[]): string =>
  [`${config.chatApiUrl ?? ''}${contactId}`, 'attachments', ...path].join('/');

const authHeaders = (accessToken: string, connectionToken: string): Record<string, string> => ({
  Authorization: `Bearer ${accessToken}`,
  [CONNECTION_TOKEN_HEADER]: connectionToken
});

const throwApiError = async (response: Response): Promise<never> => {
  const problem = (await response.json().catch(() => ({}))) as { code?: string };
  throw new ChatAttachmentApiError(response.status, problem.code);
};

export const startChatAttachmentUpload = async (
  accessToken: string,
  connectionToken: string,
  contactId: string,
  file: File
): Promise<ChatAttachmentUpload> => {
  const response = await fetch(attachmentsUrl(contactId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(accessToken, connectionToken) },
    body: JSON.stringify({ fileName: file.name, contentType: file.type, fileSizeInBytes: file.size })
  });

  if (!response.ok) return throwApiError(response);

  return (await response.json()) as ChatAttachmentUpload;
};

export const uploadChatAttachmentBytes = async (upload: ChatAttachmentUpload['upload'], file: File): Promise<void> => {
  const response = await fetch(upload.href, { method: 'PUT', headers: upload.headers, body: file });

  if (!response.ok) throw new Error(`Upload to S3 responded with status ${response.status}`);
};

export const confirmChatAttachmentUpload = async (
  accessToken: string,
  connectionToken: string,
  contactId: string,
  attachmentId: string
): Promise<void> => {
  const response = await fetch(attachmentsUrl(contactId, attachmentId, 'upload-confirmations'), {
    method: 'POST',
    headers: authHeaders(accessToken, connectionToken)
  });

  if (!response.ok) return throwApiError(response);
};

export const fetchChatAttachmentDownload = async (
  accessToken: string,
  connectionToken: string,
  contactId: string,
  attachmentId: string
): Promise<ChatAttachmentDownload> => {
  const response = await fetch(attachmentsUrl(contactId, attachmentId), {
    method: 'GET',
    headers: authHeaders(accessToken, connectionToken)
  });

  if (!response.ok) return throwApiError(response);

  return (await response.json()) as ChatAttachmentDownload;
};
